import { useMemo } from 'react';

export type DDayResult =
  | {
      label: '오늘' | '까지' | '로부터';
      day: number;
      isToday: boolean;
      rawDiff: number;
      sign: string;
    }
  | undefined;

interface UseDDayOptions {
  fromDate?: Date | null;
  toDate?: Date | null;
}

export function toSafeDate(input: any): Date {
  if (input instanceof Date) {
    return isNaN(input.getTime()) ? new Date() : input;
  }

  // Firestore Timestamp 구조 대응
  if (
    typeof input === 'object' &&
    input !== null &&
    typeof input.seconds === 'number' &&
    typeof input.nanoseconds === 'number'
  ) {
    return new Date(input.seconds * 1000);
  }

  if (typeof input === 'number' || typeof input === 'string') {
    const date = new Date(input);
    return isNaN(date.getTime()) ? new Date() : date;
  }

  return new Date(); // fallback to now
}

function convertGetTime(date: Date): number {
  const d = toSafeDate(date);
  return d.setHours(0, 0, 0, 0);
}

export function useDDay({ fromDate, toDate }: UseDDayOptions): DDayResult {
  return useMemo(() => {
    const now = Date.now();
    const baseFrom = toSafeDate(fromDate ?? now);
    const baseTo = toSafeDate(toDate ?? now);

    const _from = convertGetTime(baseFrom);

    const _to = convertGetTime(baseTo);
    const rawDiff = _to - _from;
    const dayDiff = Math.floor(rawDiff / (1000 * 60 * 60 * 24));

    if (Number.isNaN(dayDiff)) return;

    const isToday = dayDiff === 0;

    // 카운트다운: toDate가 있는 경우(까지), 그 외는 로부터
    const isCountdown = (!fromDate && toDate) || (fromDate && toDate);

    if (isToday) {
      if (!isCountdown) {
        // "로부터" 카운트: 시작일 당일을 1일로 카운트
        return {
          label: '로부터',
          day: 1,
          isToday: true,
          rawDiff,
          sign: 'D + ',
        };
      }
      return {
        label: '오늘',
        day: 0,
        isToday: true,
        rawDiff,
        sign: 'D - Day',
      };
    }

    // 판단 기준: toDate가 미래거나 둘 다 있을 경우 → '까지'
    const label = isCountdown ? '까지' : '로부터';
    const sign = isCountdown ? 'D - ' : 'D + ';

    return {
      label,
      // "로부터" 카운트는 시작일을 1일로 치므로 +1
      day: isCountdown ? Math.abs(dayDiff) : Math.abs(dayDiff) + 1,
      isToday: false,
      rawDiff,
      sign,
    };
  }, [fromDate, toDate]);
}
